import {
  Box,
  CardContent,
  Divider,
  Grid2 as Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { BarAVGCoachResponse } from "./BarAVGCoachResponse";
import { CardOutline } from "components/Card";
import { AVGTimeResponseInfo } from "./AVGTimeResponseInfo";
import { QuestionMark } from "@mui/icons-material";
import { CoachesFeedbackTable } from "./CoachesFeedbackTable";
import { useGetFeedbacksByOrganizationIdLazyQuery } from "../../../graphql/__generated__/hooks";
import {
  FeedbackStatus,
  FeedbackType,
  ProcessedFeedback,
} from "../../../types/feedback";
import { FEEDBACK_RESPONSE_TIME_CATEGORY } from "../../../helpers/constants";
import { formatTime } from "../utils";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { organizationSelectedSelector } from "../../../reduxStore/organization/selectors";

export const CoachesFeedback: FC = () => {
  const organization = useAppSelector(organizationSelectedSelector);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [categoriesCount, setCategoriesCount] = useState({
    [FEEDBACK_RESPONSE_TIME_CATEGORY.FAST]: 0,
    [FEEDBACK_RESPONSE_TIME_CATEGORY.MODERATE]: 0,
    [FEEDBACK_RESPONSE_TIME_CATEGORY.SLOW]: 0,
    [FEEDBACK_RESPONSE_TIME_CATEGORY.VERY_SLOW]: 0,
  });

  const [stats, setStats] = useState({
    avgTime: "0h 0m",
    totalFeedbacks: 0,
    done: 0,
    pending: 0,
    declined: 0,
  });

  const [processedFeedbacks, setProcessedFeedbacks] = useState(
    [] as ProcessedFeedback[],
  );

  const [getFeedbacks] = useGetFeedbacksByOrganizationIdLazyQuery();

  useEffect(() => {
    getFeedbacks({ variables: { organizationId: organization?.org_id } }).then(
      (res) => {
        if (res.data?.feedback?.length === 0) {
          console.log("No feedbacks found");
        } else {
          processFeedbacks(res.data?.feedback as FeedbackType[]);
        }
      },
    );
  }, [organization]);

  const processFeedbacks = (feedbacks: FeedbackType[]) => {
    const feedbacksByCoach: any = {} as Record<string, FeedbackType[]>;
    if (!feedbacks) {
      return;
    }
    for (const feedback of feedbacks) {
      if (feedback.coach_id) {
        if (!feedbacksByCoach[feedback.coach_id]) {
          feedbacksByCoach[feedback.coach_id] = [];
        }
        feedbacksByCoach[feedback.coach_id].push(feedback);
      }
    }
    const processedFeedbacks: ProcessedFeedback[] = Object.entries(
      feedbacksByCoach,
    ).map(([coachId, fb]) => {
      const feedbacks = fb as FeedbackType[];
      const reviewedFeedbacks = feedbacks.filter(
        (feedback) => feedback.reviewed_at && feedback.created_at,
      );
      const avgResponseTime = calculateResponseTimeAverage(reviewedFeedbacks);
      const coach = feedbacks[0]?.coach;
      if (!coach) throw new Error(`No coach found for feedback ${coachId}`);
      return {
        coachId,
        avgResponseTime,
        feedbacks,
        coach,
        category: getFeedbackTimeResponseCategory(avgResponseTime),
      };
    });
    setProcessedFeedbacks(processedFeedbacks);

    const categoryCounts = {
      [FEEDBACK_RESPONSE_TIME_CATEGORY.FAST]: processedFeedbacks.filter(
        (entry) => entry.category === FEEDBACK_RESPONSE_TIME_CATEGORY.FAST,
      ).length,
      [FEEDBACK_RESPONSE_TIME_CATEGORY.MODERATE]: processedFeedbacks.filter(
        (entry) => entry.category === FEEDBACK_RESPONSE_TIME_CATEGORY.MODERATE,
      ).length,
      [FEEDBACK_RESPONSE_TIME_CATEGORY.SLOW]: processedFeedbacks.filter(
        (entry) => entry.category === FEEDBACK_RESPONSE_TIME_CATEGORY.SLOW,
      ).length,
      [FEEDBACK_RESPONSE_TIME_CATEGORY.VERY_SLOW]: processedFeedbacks.filter(
        (entry) => entry.category === FEEDBACK_RESPONSE_TIME_CATEGORY.VERY_SLOW,
      ).length,
    };
    setCategoriesCount(categoryCounts);

    const stats = {
      avgTime: calculateTheFeedbackResponseTimeAvgAndFormat(
        processedFeedbacks
          .filter((f) => f.avgResponseTime)
          .map((f) => f.avgResponseTime),
      ),
      totalFeedbacks: feedbacks.length,
      done: feedbacks.filter(
        (feedback) => feedback.status === FeedbackStatus.DONE,
      ).length,
      pending: feedbacks.filter(
        (feedback) => feedback.status === FeedbackStatus.PENDING,
      ).length,
      declined: feedbacks.filter(
        (feedback) => feedback.status === FeedbackStatus.DECLINED,
      ).length,
    };
    setStats(stats);
  };

  const calculateTheFeedbackResponseTimeAvgAndFormat = (
    responseTimes: number[],
  ) => {
    const nonZeroResponseTimes = responseTimes.filter((time) => time > 0);
    const avgResponseTime =
      nonZeroResponseTimes.reduce((a, b) => a + b, 0) /
      nonZeroResponseTimes.length;
    return formatTime(avgResponseTime);
  };

  const getFeedbackTimeResponseCategory = (responseTime: number) => {
    if (responseTime <= 6) {
      return FEEDBACK_RESPONSE_TIME_CATEGORY.FAST;
    } else if (responseTime <= 24) {
      return FEEDBACK_RESPONSE_TIME_CATEGORY.MODERATE;
    } else if (responseTime <= 48) {
      return FEEDBACK_RESPONSE_TIME_CATEGORY.SLOW;
    } else {
      return FEEDBACK_RESPONSE_TIME_CATEGORY.VERY_SLOW;
    }
  };

  const calculateResponseTimeAverage = (feedbacks: FeedbackType[]) => {
    const responseTimes = feedbacks.map((feedback) => {
      const createdAt = new Date(feedback.created_at || "");
      const reviewedAt = new Date(feedback.reviewed_at || "");
      return (reviewedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    });
    return responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ lg: 8, xs: 12 }}>
          <CardOutline sx={{ height: "100%" }}>
            <CardContent>
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                >
                  <Typography variant="h5" gutterBottom>
                    AVG Time Response
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsInfoModalOpen(true)}
                    sx={{
                      backgroundColor: "secondary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "secondary.dark",
                      },
                    }}
                  >
                    <QuestionMark fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
              <Divider sx={{ margin: "1rem 0" }} />
              <BarAVGCoachResponse data={categoriesCount} />
            </CardContent>
          </CardOutline>
        </Grid>
        <Grid size={{ lg: 4, xs: 12 }}>
          <CardOutline sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Stats
              </Typography>
              <Divider sx={{ margin: "1rem 0" }} />
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>AVG Response Time:</strong>
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Feedbacks:</strong>
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Done:</strong>
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Pending:</strong>
                  </Typography>
                  <Typography variant="body1">
                    <strong>Declined:</strong>
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    {stats.avgTime}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {stats.totalFeedbacks}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {stats.done}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {stats.pending}
                  </Typography>
                  <Typography variant="body1">{stats.declined}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </CardOutline>
        </Grid>
        <Grid size={{ lg: 12, xs: 12 }}>
          <CardOutline sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Coaches
              </Typography>
              <Divider sx={{ margin: "1rem 0" }} />
              <CoachesFeedbackTable processedFeedbacks={processedFeedbacks} />
            </CardContent>
          </CardOutline>
        </Grid>
      </Grid>
      <AVGTimeResponseInfo
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </>
  );
};
